import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  RegularModal as BaseRegularModal,
  DatePicker,
  NumberInput,
  Input,
  Button,
  Checkbox,
  UserSelect,
} from '@/components_deprecated'
import VehicleSelect from '@/components/vehicle-select'
import { addHours, differenceInWeeks, startOfDay } from 'date-fns'
import { Field, Form } from '@/modules/users/user-settings/components'
import type { VehicleDocumentNode } from '@/modules/vehicles'
import type {
  EditVehicleDocumentForm,
  EditVehicleDocumentPromptResolve,
} from '@/modules/vehicles/types'
import { showFileDialog } from '@/utils/file'
import styled from 'styled-components'
import { useState } from 'react'

const UploadWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const UploadButton = styled(Button)`
  background-color: ${props => props.theme.colors.gray8};
  color: ${props => props.theme.colors.gray3};
  padding: 1rem 1.2rem;
  height: 43px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  &:hover {
    background-color: ${props => props.theme.colors.gray9};
  }
`
const Filename = styled.div`
  padding: 0 1rem;
  max-width: 70%;
`
const RegularModal = styled(BaseRegularModal)`
  box-shadow: ${props => props.theme.shadow.default};
`

interface EditVehicleDocumentModalProps {
  vehicleDocument?: VehicleDocumentNode
  /**
   * Whether to ask which vehicle the document belongs to. On for the
   * fleet-wide documents page, off on a vehicle's own tab, which supplies it
   * itself.
   */
  askForVehicle?: boolean
  onSubmit: PromptResolve<EditVehicleDocumentPromptResolve | null>
}

export const EditVehicleDocumentModal: React.FC<
  EditVehicleDocumentModalProps
> = ({ vehicleDocument, askForVehicle = false, onSubmit }) => {
  const translations = useTranslate({
    newDocument: 'vehicles.new-vehicle-document',
    editDocument: 'vehicles.edit-vehicle-document',
    vehicle: 'common.vehicle',
    name: 'common.name',
    description: 'common.description',
    hasNotification: 'settings.has-notification',
    expirationDate: 'documents.expiration-date',
    notificationTime: 'documents.notification-time',
    uploadFile: 'documents.upload-file',
    editFile: 'documents.edit-file',
    responsibleUser: 'common.responsible-user',
    validation: {
      required: 'common.required',
      noFileSelected: 'vehicles.no-file-selected',
    },
  })

  const editing = typeof vehicleDocument !== 'undefined'
  const [hasNotification, setHasNotification] = useState(
    !!vehicleDocument?.notificationTime || false
  )

  const expirationDate = vehicleDocument?.expirationDate
    ? new Date(vehicleDocument.expirationDate)
    : null
  const notificationTime =
    !vehicleDocument?.expirationDate || !vehicleDocument?.notificationTime
      ? 1
      : differenceInWeeks(
          new Date(vehicleDocument.expirationDate),
          startOfDay(new Date(vehicleDocument.notificationTime))
        )

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    updateForm,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<EditVehicleDocumentForm>({
    values: {
      name: vehicleDocument?.name || '',
      description: vehicleDocument?.description || '',
      expirationDate: expirationDate,
      notificationTime: notificationTime,
      file: vehicleDocument?.file || null,
      responsibleUser: vehicleDocument?.responsibleUser?.id || null,
      vehicle: vehicleDocument?.vehicle?.id ?? null,
    },
    validators: {
      name: val => (val ? null : translations.validation.required),
      file: val => (val ? null : translations.validation.noFileSelected),
      vehicle: val =>
        !askForVehicle || val ? null : translations.validation.required,
    },
  })

  async function handleUpload(files: File[]) {
    const file = files[0]
    if (!file) return

    updateForm({
      name: file.name,
      file,
    })
  }

  return (
    <RegularModal
      title={editing ? translations.editDocument : translations.newDocument}
      width="480px"
      // The form starts out valid (there is nothing to validate until the user
      // touches it), so the two things a document cannot be created without are
      // checked here as well as by the validators.
      submitDisabled={
        !formValid || !form.file || (askForVehicle && !form.vehicle)
      }
      submitText="Save"
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      onSubmit={submit(form => onSubmit({ form, hasNotification }))}
    >
      <Form>
        {askForVehicle && (
          <Field>
            <label>{translations.vehicle}</label>
            <VehicleSelect
              value={form.vehicle ?? ''}
              className="mt-2"
              clearable
              onChange={vehicle => handler('vehicle')(vehicle?.id ?? null)}
            />
            {errors.vehicle && (
              <span className="text-sm text-red-600">{errors.vehicle}</span>
            )}
          </Field>
        )}

        {!!form.file && (
          <>
            <Field>
              <label>{translations.name}</label>
              <Input
                value={form.name}
                error={errors.name}
                fullWidth
                autoFocus
                onChange={handler('name')}
              />
            </Field>
            <Field>
              <label>{translations.description}</label>
              <Input
                value={form.description}
                error={errors.description}
                fullWidth
                autoFocus
                onChange={handler('description')}
              />
            </Field>
          </>
        )}
        {!editing && (
          <Field>
            <label>{translations.uploadFile}</label>

            <UploadWrapper>
              <UploadButton
                iconLeftProps={{ icon: 'paperclip' }}
                onClick={() => showFileDialog(handleUpload)}
              >
                {form.file ? translations.editFile : translations.uploadFile}
              </UploadButton>
              {!!form.file && (
                <Filename>
                  {typeof form.file === 'object' && form.file.name}
                </Filename>
              )}
            </UploadWrapper>
          </Field>
        )}
        {!!form.file && (
          <Field alignLeft>
            <Checkbox
              checked={hasNotification}
              label={translations.hasNotification}
              boxSize="32px"
              onChange={setHasNotification}
            />
          </Field>
        )}
        {!!form.file && hasNotification && (
          <>
            <Field>
              <label>{translations.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser}
                fullWidth
                onChange={handler('responsibleUser')}
                nullable
              />
            </Field>
            <Field>
              <label>{translations.expirationDate}</label>
              <DatePicker
                value={form.expirationDate ?? new Date()}
                fullWidth
                onChange={value => {
                  const newDate = addHours(value, 9)
                  updateForm({ expirationDate: newDate })
                }}
              />
            </Field>
            <Field>
              <label>{translations.notificationTime}</label>
              <NumberInput
                value={form.notificationTime ?? 1}
                min={0}
                fullWidth
                onChange={handler('notificationTime')}
              />
            </Field>
          </>
        )}
      </Form>
    </RegularModal>
  )
}
